import { z } from 'zod'
import axios from 'axios'
import { parseRequestError } from '@/common/utilities'

const CreateHelpLinkResponseSchema = z.object({
  id: z.number(),
  program_id: z.number(),
  link: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
})

export type CreateHelpLinkResponse = z.infer<
  typeof CreateHelpLinkResponseSchema
>

const HelpLinkSchema = z.object({
  id: z.number(),
  program_id: z.number(),
  link: z.string().url(),
})

const PaginationLinkSchema = z.object({
  url: z.string().nullable(),
  label: z.string(),
  active: z.boolean(),
})

const HelpLinksApiResponseSchema = z.object({
  current_page: z.number(),
  data: z.array(HelpLinkSchema),
  first_page_url: z.string().url(),
  from: z.number().nullable(),
  last_page: z.number(),
  last_page_url: z.string().url(),
  links: z.array(PaginationLinkSchema),
  next_page_url: z.string().url().nullable(),
  path: z.string().url(),
  per_page: z.number(),
  prev_page_url: z.string().url().nullable(),
  to: z.number().nullable(),
  total: z.number(),
})

export const HelpLinksSchema = z.array(HelpLinkSchema)

const UpdateHelpLinkResponseSchema = z.object({
  id: z.number(),
  program_id: z.number(),
  link: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  name: z.string(),
})

export type UpdateHelpLinkResponse = z.infer<
  typeof UpdateHelpLinkResponseSchema
>

export const createHelpLink = async (
  program_id: number,
  link: string,
  globalOnId: number = 1
): Promise<CreateHelpLinkResponse> => {
  try {
    const url = `/help_links?global_on_id=${globalOnId}`

    const payload = {
      program_id: program_id,
      link,
    }

    const response = await axios.post(url, payload)

    return CreateHelpLinkResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const updateHelpLink = async (
  id: number,
  link: string,
  globalOnId: number = 1
): Promise<UpdateHelpLinkResponse> => {
  try {
    const url = `/help_links/${id}?global_on_id=${globalOnId}`

    const payload = {
      link,
    }

    const response = await axios.put(url, payload)

    return UpdateHelpLinkResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export async function GETHelpLinks(
  globalOnId: number,
  params: { program_id?: number; search?: string }
) {
  const response = await axios.get('/help_links', {
    params: { global_on_id: globalOnId, ...params },
  })

  return HelpLinksApiResponseSchema.parse(response.data)
}

export async function GETHandbookLinks(
  globalOnId: number,
  params: { program_id?: number }
) {
  try {
    const response = await axios.get('/provider_handbook_links', {
      params: { global_on_id: globalOnId, ...params },
    })

    return HelpLinksApiResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(`Error fetching provider handbook links: ${error}`)
  }
}

export const createHandbookLink = async (
  program_id: number,
  link: string,
  globalOnId: number = 1
): Promise<CreateHelpLinkResponse> => {
  try {
    const url = `/provider_handbook_links?global_on_id=${globalOnId}`

    const payload = {
      program_id: program_id,
      link,
    }

    const response = await axios.post(url, payload)

    return CreateHelpLinkResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const updateHandbookLink = async (
  id: number,
  link: string,
  globalOnId: number = 1
): Promise<UpdateHelpLinkResponse> => {
  try {
    const url = `/provider_handbook_links/${id}?global_on_id=${globalOnId}`

    const payload = {
      link,
    }

    const response = await axios.put(url, payload)

    return UpdateHelpLinkResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export async function GETAccessHandbookLinks(
  globalOnId: number,
  params: { program_id?: number }
) {
  try {
    const response = await axios.get('/access_handbook_links', {
      params: { global_on_id: globalOnId, ...params },
    })

    // Parse and validate the response data using the schema
    return HelpLinksApiResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const createAccessHandbookLink = async (
  program_id: number,
  link: string,
  globalOnId: number = 1
): Promise<CreateHelpLinkResponse> => {
  try {
    const url = `/access_handbook_links?global_on_id=${globalOnId}`

    const payload = {
      program_id: program_id,
      link,
    }

    const response = await axios.post(url, payload)

    return CreateHelpLinkResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const updateAccessHandbookLink = async (
  id: number,
  link: string,
  globalOnId: number = 1
): Promise<UpdateHelpLinkResponse> => {
  try {
    const url = `/access_handbook_links/${id}?global_on_id=${globalOnId}`

    const payload = {
      link,
    }

    const response = await axios.put(url, payload)

    return UpdateHelpLinkResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}
